import { gql, useMutation } from '@apollo/client';
import { useDispatch, useSelector } from 'react-redux';

import { tileMetricsFragment } from 'api/fragments/workflows';
import { SaveJourneySettingsResponse } from 'modules/journey/components/SettingsTakeover/hooks';
import { addNegativeToastWithErrorId, addPositiveToast } from 'modules/toastContainer';
import {
  fetchSendMetricsRequest,
  getFlexMetricsDateRange,
  getWorkflow,
  updateWorkflow,
} from 'modules/workflows/ducks';
import { WorkflowType } from 'modules/workflows/enums';
import { MetricDefinitionsBySendAction, MetricNamesBySendAction } from 'modules/workflows/types';

const SUCCESS_MESSAGE = 'Success! Metrics settings updated.';
const FAILURE_MESSAGE = `Error updating metrics settings. Please try again later.`;

export const saveMetricsMutation = gql`
  mutation saveTileMetrics(
    $journeyId: Long!
    $journeyType: WorkflowType!
    $metricNamesInput: MetricNamesInputInput!
  ) {
    saveTileMetrics(
      journeyId: $journeyId
      journeyType: $journeyType
      metricNamesInput: $metricNamesInput
    ) {
      updatedTileMetrics {
        ...TileMetricsFragment
      }
    }
  }
  ${tileMetricsFragment}
`;

type useUpdateTileMetricsProps = {
  sendMetrics: {
    journeyId: number;
    journeyType: WorkflowType;
    metricNamesInput: MetricNamesBySendAction;
  };
  /** Callback when the update was successful */
  onSuccess?: () => void;
  /** Should a success message show? */
  showSuccessToast?: boolean;
  /** Should the metrics be refetched after a successful update? */
  fetchMetricsOnSuccess?: boolean;
};

type updateTileMetricsResponse = {
  saveTileMetrics: {
    updatedTileMetrics: MetricDefinitionsBySendAction;
  };
};

export const useUpdateTileMetrics = () => {
  const dispatch = useDispatch();
  const flexMetricsDateRange = useSelector(getFlexMetricsDateRange);
  const workflow = useSelector(getWorkflow);

  const [saveSendMetrics] = useMutation<updateTileMetricsResponse>(saveMetricsMutation);

  return async ({
    sendMetrics,
    onSuccess,
    showSuccessToast = true,
    fetchMetricsOnSuccess = true,
  }: useUpdateTileMetricsProps): Promise<SaveJourneySettingsResponse> => {
    try {
      const { data } = await saveSendMetrics({
        variables: sendMetrics,
      });
      const tileMetrics = data?.saveTileMetrics?.updatedTileMetrics;

      if (tileMetrics) {
        const updatedWorkflow = { ...workflow, tileMetrics };
        dispatch(updateWorkflow({ tileMetrics }, { suppressHasUnsavedChangesUpdate: true }));
        if (onSuccess) onSuccess();
        if (showSuccessToast) dispatch(addPositiveToast(SUCCESS_MESSAGE));
        if (fetchMetricsOnSuccess) {
          dispatch(fetchSendMetricsRequest(updatedWorkflow, flexMetricsDateRange));
        }
      } else {
        dispatch(addNegativeToastWithErrorId(FAILURE_MESSAGE, 'useUpdateTileMetrics'));
      }
      return { hasErrors: !tileMetrics };
    } catch {
      dispatch(addNegativeToastWithErrorId(FAILURE_MESSAGE, 'useUpdateTileMetrics'));
      return { hasErrors: true };
    }
  };
};
